import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronDown, ShieldCheck, Award, Infinity as InfinityIcon, BookOpen, Smartphone, GraduationCap, Play, Lock } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import useMeta from "@/hooks/useMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCT_LOOKUP_KEY = "c_leadership";

const AUDIENCE = [
  { role: "For emerging & senior leaders", body: "Emerging leaders preparing for greater responsibility, and executive and senior leaders committed to leading with integrity at every level." },
  { role: "For HR, coaches & educators", body: "HR professionals, organizational development practitioners, leadership coaches, consultants, and educators looking for frameworks that train others and build a healthier culture." },
  { role: "For mission-driven leaders", body: "Military and government leaders, nonprofit and faith-based leaders, and business owners and entrepreneurs who believe leadership begins with personal responsibility and continuous growth." },
];

const OUTCOMES = [
  { t: "Build trust", b: "Move beyond performance metrics: cultivate the internal disciplines that turn managers into leaders others willingly follow." },
  { t: "Strengthen emotional intelligence", b: "Deepen your skill across all four domains of emotional intelligence: self-awareness, self-management, social awareness, and relationship management." },
  { t: "Improve decision-making", b: "Apply frameworks that keep you clear and principled when the stakes are highest." },
  { t: "Develop resilience", b: "Build practical routines for navigating organizational change and sustained pressure without losing yourself." },
  { t: "Align with your values", b: "Turn abstract values into a concrete operating system you can lead by every day." },
  { t: "Lead with authenticity", b: "Learn to be the same leader in private that you are in front of others." },
  { t: "Create legacy impact", b: "Build character-based leadership that extends beyond your tenure, and leave with a plan for the impact you want to be known for." },
];

const MODULES = [
  { t: "Introduction: the core of self-leadership", time: "~30 min", b: "The course opens with the central premise: leadership is not defined by position, authority, or title — it is defined by character. You will build the foundational mindset that leadership begins with the internal work of leading yourself." },
  { t: "Building trust through internal discipline", time: "~40 min", b: "Move beyond performance metrics and learn the internal disciplines — consistency, integrity, and follow-through — that turn managers into leaders others willingly follow." },
  { t: "Emotional intelligence for self-leadership", time: "~50 min", b: "A deep dive into the four domains of emotional intelligence: self-awareness, self-management, social awareness, and relationship management." },
  { t: "Principled decision-making under pressure", time: "~40 min", b: "Learn frameworks that keep you clear and principled when the stakes are highest. A repeatable process for making sound decisions when time is short." },
  { t: "Resilience and navigating change", time: "~35 min", b: "Practical routines for staying grounded through organizational change, setbacks, and sustained pressure — habits that protect your character under stress." },
  { t: "Values alignment and authentic leadership", time: "~45 min", b: "Turn abstract values into a concrete operating system you can lead by every day. Includes a values-clarification exercise you can use immediately." },
  { t: "Actionable strategy: your legacy impact", time: "~40 min", b: "The capstone module. You will build a personal character-leadership plan, a document you leave the course with, designed to extend your influence well beyond your current role." },
];

const INCLUDED = [
  { icon: Play,          l: "Full video course", s: "7 modules · 4–6 hrs" },
  { icon: BookOpen,      l: "Companion workbook", s: "Exercises & frameworks" },
  { icon: Award,         l: "Signed certificate", s: "Personalized & signed" },
  { icon: InfinityIcon,  l: "Lifetime access", s: "Including future updates" },
  { icon: Smartphone,    l: "Learn anywhere", s: "Desktop, tablet & mobile" },
];

const TESTIMONIALS = [
  { q: "Dr. Campbell has a rare gift: he makes character development feel practical, not abstract. I walked away with habits I actually use, not just ideas I agreed with.", a: "Senior Manager, Federal Agency", r: "Leadership development program participant" },
  { q: "What sets Dr. Campbell apart is that he has lived this. He is not teaching from a textbook; he is teaching from decades of leading under real pressure. That credibility changes everything.", a: "HR Director, Healthcare System", r: "Executive leadership workshop attendee" },
  { q: "The PACT framework and the values-alignment exercise gave me language I still use with my team today. It changed how I show up, not just what I say.", a: "Team Lead, Technology Company", r: "Character Code workshop graduate" },
];

const FAQS = [
  { q: "How long do I have access to the course?", a: "You have lifetime access from the moment you enroll, including all future updates. No subscriptions, no renewal fees, no expiry dates." },
  { q: "Is this course truly self-paced?", a: "Yes. No live sessions, no deadlines, no cohort schedule. Learn entirely on your own timeline." },
  { q: "What is the time commitment?", a: "Approximately 4–6 hours of video across seven modules. With workbook exercises, most learners report 6–8 hours total." },
  { q: "How does the certificate work?", a: "Upon completing all seven modules, you receive a personalized Certificate of Completion, signed by Dr. Andrew H. Campbell, from the Global Leadership Education and Training Institute." },
  { q: "What is the refund policy?", a: "Full 30-day money-back guarantee with no conditions. Email refunds@globalleadersolutions.com within 30 days and your refund will be processed within 5–7 business days." },
  { q: "Is checkout secure?", a: "Yes. All transactions are processed through SSL-encrypted, PCI-compliant Stripe infrastructure. Immediate enrollment confirmation upon purchase." },
  { q: "Can I purchase this for my team?", a: "Yes. Group licensing and organizational pricing available for teams of 5+. Contact corporate@globalleadersolutions.com." },
];

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`py-10 sm:py-14 ${className}`}>
    <div className="max-w-6xl mx-auto px-6 sm:px-8">{children}</div>
  </section>
);

const Label = ({ children, tone = "rose" }) => (
  <div className={`text-[10px] tracking-[0.28em] uppercase mb-3 ${tone === "gold" ? "text-[#C9A961]" : "text-[#8C4A57]"}`}>{children}</div>
);

// Inline email-capture + cart hand-off used across the /character page.
// Captures the lead into the newsletter list (fire-and-forget), adds the
// Character Code course to the shopping cart, and hands the buyer to /cart
// — matching how the rest of the site checks out.
const EnrollBox = ({ children, className = "", variant = "light", size = "md", block = false, testid = "character-enroll-box" }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) { toast.error("Enter your email to enroll."); return; }
    setBusy(true);
    try {
      // 1) Save email the usual way (fire-and-forget; failure shouldn't block the cart hand-off)
      subscribeNewsletter({ email: value, source: "character-landing" })
        .catch((err) => console.warn("newsletter capture failed", err));
      // 2) Fetch Character Code product and add it to the cart, then hand off
      //    to /cart with the buyer's email prefilled so they can check out
      //    the same way the rest of the site does.
      const { data: product } = await axios.get(`${API}/products/${PRODUCT_LOOKUP_KEY}`);
      addItem(product);
      toast.success(`${product.title} added to cart`);
      navigate(`/cart?email=${encodeURIComponent(value)}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't start enrollment. Please try again.";
      toast.error(msg);
      setBusy(false);
    }
  };

  const dark = variant === "dark";
  const inputCls = dark
    ? "flex-1 min-w-0 px-4 py-4 bg-white/10 border border-white/30 text-white placeholder-white/50 rounded-sm focus:outline-none focus:border-[#C9A961] text-sm"
    : "flex-1 min-w-0 px-4 py-4 bg-white border border-[#E8DCDD] text-[#2D2628] placeholder-[#6B5E62] rounded-sm focus:outline-none focus:border-[#8C4A57] text-sm";
  const btnPad = size === "lg" ? "px-11 py-5 text-base" : "px-8 py-4 text-[13px]";

  return (
    <form
      onSubmit={submit}
      data-testid={testid}
      className={`flex flex-col sm:flex-row gap-2 items-stretch ${block ? "w-full" : "w-full max-w-xl"} ${className}`}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email address"
        data-testid={`${testid}-email`}
        aria-label="Your email address"
        className={inputCls}
      />
      <button
        type="submit"
        disabled={busy}
        data-testid={`${testid}-submit`}
        className={`bg-[#8C4A57] text-white tracking-[0.14em] uppercase font-semibold rounded-sm hover:bg-[#6f3945] transition disabled:opacity-70 disabled:cursor-not-allowed ${btnPad} whitespace-nowrap`}
      >
        {busy ? "Adding…" : children}
      </button>
    </form>
  );
};

const Accordion = ({ items, testidPrefix }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} data-testid={`${testidPrefix}-${i}`} className="border border-[#E8DCDD] rounded-sm bg-white">
          <button onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[#FAF4F5] transition">
            {it.num !== undefined && <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8C4A57] text-white text-sm font-semibold flex items-center justify-center">{it.num}</span>}
            <span className="flex-1 font-serif text-[#2D2628] text-base sm:text-lg">{it.t || it.q}</span>
            {it.time && <span className="text-[10px] tracking-widest uppercase text-[#8C4A57] hidden sm:inline">{it.time}</span>}
            <ChevronDown className={`w-5 h-5 text-[#8C4A57] transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && <div className="px-5 pb-5 pl-16 sm:pl-16 text-[15px] leading-relaxed text-[#4A3E42]">{it.b || it.a}</div>}
        </div>
      ))}
    </div>
  );
};

export default function LandingCharacter() {
  useMeta({
    title: "Character Code: The Core of Self-Leadership — Dr. Andrew Campbell",
    description: "A transformative self-leadership course. 7 modules · workbook · signed certificate · 30-day guarantee.",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-white text-[#2D2628]">
      {/* Announcement bar */}
      <div className="bg-[#0F1A2E] text-white text-center text-xs sm:text-sm py-2 px-4">
        <strong>Certificate of Completion included.</strong> Enroll today and start building the character leadership requires. <span className="hidden sm:inline">· Companion workbook included.</span>
      </div>

      {/* HERO */}
      <Section id="top" className="!pt-6 !pb-10 bg-gradient-to-b from-[#FAF4F5] to-white">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-8 items-start">
          <div>
            <Label>A transformative self-leadership course</Label>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2628] leading-[1.08] mb-4">
              Character Code:<br/>The Core of <em className="text-[#8C4A57]">Self-Leadership</em>
            </h1>
            <p className="text-lg text-[#4A3E42] leading-relaxed mb-4 max-w-2xl">Titles are given. Trust is earned. Character is built. This course teaches you how.</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {["For emerging & senior leaders","HR & organizational development","Coaches, consultants & educators"].map(p => (
                <span key={p} className="text-[11px] tracking-wider uppercase text-[#8C4A57] border border-[#E8DCDD] bg-white px-3 py-1.5 rounded-full">{p}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 items-center mb-2">
              <EnrollBox testid="character-enroll-hero">Enroll Now for $225.99</EnrollBox>
              <a href="#preview" className="inline-block border border-[#8C4A57] text-[#8C4A57] text-[13px] tracking-[0.14em] uppercase font-semibold px-7 py-4 rounded-sm hover:bg-[#8C4A57] hover:text-white transition">Watch Preview</a>
            </div>
            <p className="text-[13px] text-[#6B5E62] italic mb-4">30-day money-back guarantee, no questions asked</p>
            <a
              href="#instructor"
              data-testid="character-hero-instructor-byline"
              className="inline-flex items-center gap-3 group"
            >
              <img
                src="/api/static/assets/instructor-campbell-portrait.webp"
                alt="Dr. Andrew H. Campbell"
                className="w-14 h-14 rounded-full object-cover object-top border-2 border-white shadow-md ring-1 ring-[#8C4A57]/20"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <span>
                <span className="block text-[10px] tracking-[0.2em] uppercase text-[#8C4A57] mb-0.5">Taught by</span>
                <span className="block font-serif text-[15px] text-[#2D2628] group-hover:underline">Dr. Andrew H. Campbell, PhD</span>
              </span>
            </a>
          </div>
          {/* Price card */}
          <aside className="bg-white border border-[#E8DCDD] rounded-sm p-7 shadow-lg lg:sticky lg:top-24">
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-1">Single course</div>
            <div className="font-serif text-lg text-[#2D2628] leading-tight mb-4">Character Code: The Core of Self-Leadership</div>
            <div className="flex items-baseline gap-3 mb-5">
              <span className="text-[#6B5E62] line-through text-lg">$350</span>
              <span className="font-serif text-4xl text-[#2D2628]">$225.99</span>
              <span className="text-xs text-[#6B5E62]">one-time</span>
            </div>
            <ul className="space-y-2 mb-6 text-sm text-[#4A3E42]">
              {["Full video course, all 7 modules","Companion workbook","Personalized, signed certificate","Lifetime access & free updates","Self-paced, start anytime","30-day money-back guarantee"].map(x => (
                <li key={x} className="flex gap-2"><span className="text-[#8C4A57]">✓</span>{x}</li>
              ))}
            </ul>
            <EnrollBox block testid="character-enroll-sidecard">Enroll Now</EnrollBox>
            <p className="text-[11px] text-[#6B5E62] text-center mt-4 flex items-center justify-center gap-1"><Lock className="w-3 h-3"/> Secure checkout · Instant access</p>
          </aside>
        </div>
      </Section>

      {/* TRUST ROW — "As seen on" institutional strip */}
      <div className="border-y border-[#E8DCDD] bg-white py-8" data-testid="character-trust-row">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mb-5 text-center">
            As seen on &amp; featured in
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-[#2D2628]">
            {[
              { name: "TEDx", sub: "Speaker" },
              { name: "Yale University", sub: "Adjunct Instructor" },
              { name: "Norwich University", sub: "Adjunct Instructor" },
              { name: "Air Command & Staff College", sub: "Faculty" },
              { name: "Marquis Who's Who", sub: "2024 Inductee" },
            ].map((org) => (
              <div key={org.name} className="text-center" data-testid={`trust-${org.name.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}`}>
                <div className="font-serif text-lg sm:text-xl leading-tight">{org.name}</div>
                <div className="text-[9px] tracking-[0.24em] uppercase text-[#6B5E62] mt-1">{org.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* VIDEO PREVIEW */}
      <section id="preview" data-testid="character-preview-section" className="scroll-mt-24 py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2 text-center">Preview</div>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2D2628] text-center mb-6">Watch a short introduction</h2>
          <div className="relative w-full max-w-2xl mx-auto aspect-video border border-[#E8DCDD] rounded-sm overflow-hidden shadow-md bg-black">
            <iframe
              data-testid="character-preview-iframe"
              src="https://www.youtube.com/embed/rPSMYiJxxgg?rel=0"
              title="Character Code preview"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <div className="border-y border-[#E8DCDD] bg-[#FAF4F5] py-5">
        <div className="max-w-6xl mx-auto px-6 flex flex-wrap justify-around gap-4 text-[12px] text-[#4A3E42]">
          {[[GraduationCap,"Self-paced learning"],[InfinityIcon,"Lifetime access"],[BookOpen,"Companion workbook"],[Award,"Signed certificate"],[ShieldCheck,"30-day guarantee"]].map(([Ic, t]) => (
            <div key={t} className="flex items-center gap-2"><Ic className="w-4 h-4 text-[#8C4A57]"/>{t}</div>
          ))}
        </div>
      </div>

      {/* WHO */}
      <Section id="who">
        <Label>Who this is for</Label>
        <h2 className="font-serif text-3xl sm:text-4xl mb-3">Built for leaders who want to lead<br/>themselves before they lead others</h2>
        <div className="w-16 h-0.5 bg-[#8C4A57] mb-6"/>
        <p className="text-[#6B5E62] max-w-2xl mb-10">You can be promoted into a title. You can't be promoted into character. This course builds what no job description ever will.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {AUDIENCE.map(a => (
            <div key={a.role} className="border-t-2 border-[#8C4A57] pt-5">
              <div className="font-semibold text-[#2D2628] mb-2 text-sm">{a.role}</div>
              <p className="text-[14px] leading-relaxed text-[#4A3E42]">{a.body}</p>
            </div>
          ))}
        </div>
        <div className="bg-[#0F1A2E] rounded-sm p-10 mt-14 text-white">
          <h3 className="font-serif text-2xl mb-6">Why Choose This Course?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[["Self-Leadership First","Most leadership courses teach how to manage people. This one begins by teaching you how to manage yourself."],["Evidence-Based & Practical","Built on evidence-based research, practical frameworks, and reflective exercises you can apply the same week you learn them."],["Your Next Step","Don't just hold a title — lead with character. This is your opportunity to build a legacy that outlasts your tenure."]].map(([t,b]) => (
              <div key={t}>
                <div className="font-semibold text-[#C9A961] mb-2">{t}</div>
                <p className="text-sm text-white/70 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex justify-center"><EnrollBox testid="character-enroll-mid">Enroll Now for $225.99</EnrollBox></div>
        </div>
      </Section>

      {/* WHAT YOU'LL LEARN */}
      <Section id="learn" className="bg-[#FAF4F5]">
        <Label tone="gold">What you will learn</Label>
        <h2 className="font-serif text-3xl sm:text-4xl mb-3">Seven concrete outcomes.<br/>Zero theory for theory's sake.</h2>
        <div className="w-16 h-0.5 bg-[#8C4A57] mb-6"/>
        <p className="text-[#6B5E62] max-w-2xl mb-10">Every module is designed around a discipline you can practice the same week you learn it.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {OUTCOMES.map((o, i) => (
            <div key={i} className={`bg-white border border-[#E8DCDD] rounded-sm p-6 flex gap-4 ${i === 6 ? "md:col-span-2" : ""}`}>
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#8C4A57] text-white font-serif text-lg flex items-center justify-center">{i+1}</div>
              <div>
                <h4 className="font-serif text-xl text-[#2D2628] mb-1">{o.t}</h4>
                <p className="text-[14px] text-[#4A3E42] leading-relaxed">{o.b}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* CURRICULUM */}
      <Section id="curriculum">
        <Label>What is included</Label>
        <h2 className="font-serif text-3xl sm:text-4xl mb-3">The full curriculum</h2>
        <div className="w-16 h-0.5 bg-[#8C4A57] mb-6"/>
        <p className="text-[#6B5E62] max-w-2xl mb-8">Seven modules. Each one built around a discipline you can apply immediately. Total commitment: 4–6 hours of video plus workbook exercises.</p>
        <Accordion items={MODULES.map((m, i) => ({ ...m, num: i+1 }))} testidPrefix="module" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-12">
          {INCLUDED.map(({ icon: Ic, l, s }) => (
            <div key={l} className="text-center border border-[#E8DCDD] rounded-sm p-5 bg-white">
              <Ic className="w-6 h-6 mx-auto text-[#8C4A57] mb-2"/>
              <div className="font-semibold text-[13px] text-[#2D2628]">{l}</div>
              <div className="text-[11px] text-[#6B5E62] mt-1">{s}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* INSTRUCTOR */}
      <Section id="instructor" className="bg-[#FAF4F5]">
        <div className="grid grid-cols-1 md:grid-cols-[280px,1fr] gap-10 items-start">
          <div className="aspect-square w-full max-w-[280px] rounded-sm bg-[#E8DCDD] flex items-center justify-center text-[#8C4A57] overflow-hidden">
            <img
              src="/api/static/assets/instructor-campbell-portrait.webp"
              alt="Dr. Andrew H. Campbell"
              data-testid="instructor-portrait"
              className="w-full h-full object-cover object-top"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div>
            <Label>Your instructor</Label>
            <h2 className="font-serif text-3xl text-[#2D2628] mb-1">Dr. Andrew H. Campbell, PhD</h2>
            <p className="text-[#8C4A57] font-semibold text-sm mb-4">Doctorate of Global Leadership · Master of Diplomacy, International Conflict Management</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {["25+ Years Military & Diplomatic Leadership","Yale University","Norwich University","Air Command & Staff College","GLETI Founder","Campbell PACT Model","TEDx Speaker"].map(c => (
                <span key={c} className="text-[10px] tracking-wider uppercase text-[#8C4A57] border border-[#8C4A57]/30 bg-white px-3 py-1 rounded-full">{c}</span>
              ))}
            </div>
            <p className="text-[15px] text-[#4A3E42] leading-relaxed mb-4">With more than 25 years of leadership experience across military, diplomatic, and academic settings, Dr. Campbell has served as an adjunct instructor at Yale University, Norwich University, the Air Force Command and Staff College, and the Federal Executive Leadership Institute. He is the founder of the Global Leadership Education and Training Institute (GLETI), creator of the Campbell PACT Model, a TEDx speaker, and a 2024 Marquis Who's Who of America inductee.</p>
            <div className="mb-6" data-testid="instructor-signature-block">
              <img
                src="/api/static/assets/campbell-signature.png"
                alt="Dr. Andrew Campbell signature"
                data-testid="instructor-signature"
                className="h-16 sm:h-20 w-auto -ml-1 select-none pointer-events-none"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62] mt-1">
                Director, Global Leader Solutions
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-5 border-t border-[#E8DCDD]">
              {[["25+","Years leading leaders"],["PhD","Global Leadership"],["2","Advanced degrees"]].map(([n,l]) => (
                <div key={n}><div className="font-serif text-2xl text-[#8C4A57]">{n}</div><div className="text-[11px] text-[#6B5E62]">{l}</div></div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section id="testimonials">
        <div className="text-center">
          <Label tone="gold">Learner proof</Label>
          <h2 className="font-serif text-3xl sm:text-4xl mb-3">What leaders are saying</h2>
          <p className="text-[13px] text-[#6B5E62] max-w-2xl mx-auto mb-10 italic">This course is in early enrollment. The testimonials below reflect Dr. Campbell's broader teaching and leadership development work. Graduate testimonials will be added as cohorts complete the course.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="border border-[#E8DCDD] rounded-sm p-6 bg-white">
              <div className="text-[15px] text-[#2D2628] font-serif italic leading-relaxed mb-5">"{t.q}"</div>
              <div className="text-[13px] font-semibold text-[#8C4A57]">{t.a}</div>
              <div className="text-[11px] text-[#6B5E62]">{t.r}</div>
            </div>
          ))}
        </div>
        <p className="text-[12px] text-center text-[#6B5E62] mt-10 italic">Are you a graduate of Dr. Campbell's programs? We would love to feature your story. Contact info@globalleadersolutions.com</p>
      </Section>

      {/* PRICING */}
      <Section id="pricing" className="bg-[#FAF4F5]">
        <div className="text-center">
          <Label>Enrollment</Label>
          <h2 className="font-serif text-3xl sm:text-4xl mb-3">Choose your path to self-leadership</h2>
          <p className="text-[#6B5E62] mb-10">One-time payment. Lifetime access. Start within minutes of enrolling.</p>
        </div>
        <div className="max-w-md mx-auto bg-white border-2 border-[#8C4A57] rounded-sm p-8 shadow-xl">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2">Single course</div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-[#6B5E62] line-through text-xl">$350</span>
            <span className="font-serif text-5xl text-[#2D2628]">$225.99</span>
          </div>
          <div className="text-xs text-[#6B5E62] mb-6">One-time payment · Instant access</div>
          <ul className="space-y-2 mb-7 text-sm text-[#4A3E42]">
            {["Full 7-module video course","Companion workbook","Personalized, signed certificate","Lifetime access & updates","Mobile & desktop access","30-day money-back guarantee"].map(x => (
              <li key={x} className="flex gap-2"><span className="text-[#8C4A57]">✓</span>{x}</li>
            ))}
          </ul>
          <EnrollBox block testid="character-enroll-pricecard">Enroll for $225.99</EnrollBox>
        </div>
        <div className="max-w-2xl mx-auto text-center mt-12 bg-white border border-[#E8DCDD] rounded-sm p-8">
          <ShieldCheck className="w-10 h-10 mx-auto text-[#8C4A57] mb-3"/>
          <h4 className="font-serif text-xl mb-2">30-Day Money-Back Guarantee</h4>
          <p className="text-sm text-[#4A3E42] leading-relaxed">If you complete the course and don't feel it has changed how you lead yourself, contact us within 30 days of enrollment for a full refund — no questions asked, no hoops to jump through.</p>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl sm:text-4xl">Frequently asked questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion items={FAQS} testidPrefix="faq" />
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="bg-[#0F1A2E] text-white py-20">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif text-3xl sm:text-5xl mb-6 leading-tight">Stop leading by title.<br/>Start <em className="text-[#C9A961]">leading</em> by character.</h2>
          <p className="text-white/70 leading-relaxed mb-8">Character is not defined by position, authority, or title — it is defined by what you do when no one is watching. Enroll today and start building the leadership legacy that outlasts your tenure.</p>
          <div className="flex justify-center"><EnrollBox variant="dark" size="lg" testid="character-enroll-final">Enroll Now for $225.99</EnrollBox></div>
          <div className="text-[11px] text-white/50 mt-6 flex items-center justify-center gap-2"><Lock className="w-3 h-3"/> Secure checkout · Instant access · 30-day money-back guarantee</div>
        </div>
      </section>
    </div>
  );
}
